import React, { useEffect, useState, useCallback } from 'react';
import { Shield, Search } from 'lucide-react';
import apiClient from '../../utils/api';

const LIMIT = 50;
const ROLES = ['', 'SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'RECOVERY_OFFICER', 'COMPANY_AGENT', 'INDEPENDENT_USER', 'TRANSPORTER', 'WAREHOUSE_MANAGER', 'INVENTORY_AUDITOR'];
const TARGET_TYPES = ['', 'Company', 'User', 'ApiKey', 'BillingPlan', 'PlatformSettings', 'Shipment', 'Payment'];

const ACTION_COLOR: Record<string, string> = {
  COMPANY_CREATED: '#22c55e', COMPANY_STATUS_CHANGED: '#f59e0b', COMPANY_NOTES_UPDATED: '#94a3b8',
  COMPANY_PLAN_ASSIGNED: '#8b5cf6',
  USER_CREATED: '#3b82f6', USER_UPDATED: '#6366f1', USER_DEACTIVATED: '#ef4444',
  SETTINGS_UPDATED: '#0ea5e9',
  BILLING_PLAN_CREATED: '#10b981', BILLING_PLAN_UPDATED: '#f97316', BILLING_PLAN_DELETED: '#ef4444',
  API_KEY_GENERATED: '#06b6d4', API_KEY_REVOKED: '#f43f5e',
  WEBHOOK_URL_UPDATED: '#a855f7',
};

export const SuperAdminAuditLog: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const ACTIONS = Object.keys(ACTION_COLOR);
  const [filters, setFilters] = useState({ search: '', actorRole: '', action: '', targetType: '', dateFrom: '', dateTo: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: LIMIT, offset: page * LIMIT };
      if (filters.search) params.search = filters.search;
      if (filters.actorRole) params.actorRole = filters.actorRole;
      if (filters.action) params.action = filters.action;
      if (filters.targetType) params.targetType = filters.targetType;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      const res = await apiClient.getAuditLogs(params);
      const d = res.data.data;
      setLogs(d?.logs ?? []);
      setTotal(d?.total ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={20} /> Audit Log
        </h1>
        <span style={{ fontSize: 13, color: '#64748b' }}>{total.toLocaleString()} total entries</span>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'flex-end' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search actor, action, target..." value={filters.search}
              onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(0); }}
              style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <select value={filters.actorRole} onChange={e => { setFilters(f => ({ ...f, actorRole: e.target.value })); setPage(0); }} style={{ width: 170 }}>
            {ROLES.map(r => <option key={r} value={r}>{r || 'All Roles'}</option>)}
          </select>
          <select value={filters.action} onChange={e => { setFilters(f => ({ ...f, action: e.target.value })); setPage(0); }} style={{ width: 200 }}>
            <option value="">All Actions</option>
            {ACTIONS.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
          </select>
          <select value={filters.targetType} onChange={e => { setFilters(f => ({ ...f, targetType: e.target.value })); setPage(0); }} style={{ width: 150 }}>
            {TARGET_TYPES.map(t => <option key={t} value={t}>{t || 'All Types'}</option>)}
          </select>
          <input type="date" value={filters.dateFrom} onChange={e => { setFilters(f => ({ ...f, dateFrom: e.target.value })); setPage(0); }} title="From date" />
          <input type="date" value={filters.dateTo} onChange={e => { setFilters(f => ({ ...f, dateTo: e.target.value })); setPage(0); }} title="To date" />
          <button className="btn btn-ghost" onClick={() => { setFilters({ search: '', actorRole: '', action: '', targetType: '', dateFrom: '', dateTo: '' }); setPage(0); }}>Clear</button>
        </div>

        {loading ? <div className="loading-spinner">Loading audit logs...</div> : logs.length === 0 ? (
          <div className="empty-state"><h3>No audit log entries found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Timestamp</th><th>Actor</th><th>Role</th><th>Action</th><th>Target</th><th>IP</th><th>Details</th></tr>
                </thead>
                <tbody>
                  {logs.map((log: any) => (
                    <tr key={log.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#64748b' }}>{new Date(log.createdAt).toLocaleString()}</td>
                      <td style={{ fontSize: 13 }}>{log.actorEmail || '—'}</td>
                      <td><span className="badge badge-inactive" style={{ fontSize: 10 }}>{log.actorRole?.replace(/_/g, ' ') || '—'}</span></td>
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                          background: (ACTION_COLOR[log.action] || '#94a3b8') + '20',
                          color: ACTION_COLOR[log.action] || '#64748b',
                        }}>{log.action}</span>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {log.targetType && <span style={{ color: '#94a3b8', fontSize: 11 }}>{log.targetType} </span>}
                        {log.targetName || log.targetId || '—'}
                      </td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{log.ipAddress || '—'}</td>
                      <td style={{ fontSize: 12, color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.details ? JSON.stringify(log.details) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
